import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Search, Menu, X, Sun, ChevronDown } from "lucide-react";
import { LOGO_URL } from "../lib/api";
import SectionSponsor from "./SectionSponsor";

const NAV = [
  { to: "/classic", label: "Home" },
  { to: "/section/news", label: "Local News" },
  { to: "/section/sports", label: "High School Sports" },
  { to: "/section/entertainment", label: "Entertainment" },
  { to: "/section/columnists", label: "Columnists" },
  { to: "/events", label: "Events" },
  { to: "/community", label: "Community" },
  { to: "/e-edition", label: "e-Edition" },
  { to: "/galleries", label: "Galleries" },
];

const MARKETPLACE = [
  { to: "/marketplace", label: "Marketplace Home" },
  { to: "/loyalty", label: "Loyalty Club" },
  { to: "/directory", label: "Business Directory" },
  { to: "/classifieds", label: "Classifieds" },
  { to: "/contests", label: "Contests" },
  { to: "/pets", label: "Adoptable Pets" },
];

export default function Masthead() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [mpOpen, setMpOpen] = useState(false);
  const location = useLocation();
  const mpActive = MARKETPLACE.some((m) => location.pathname.startsWith(m.to));
  const today = new Date().toLocaleDateString("en-US", {
    weekday: "long", year: "numeric", month: "long", day: "numeric",
  });

  useEffect(() => {
    setMobileOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  return (
    <header className="sticky top-0 z-50">
      {/* Utility bar */}
      <div className="bg-[var(--navy)] text-white/90">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-1.5 text-xs">
          <div className="font-ui flex items-center gap-4">
            <span className="hidden items-center gap-1.5 sm:flex">
              <Sun size={13} className="text-[var(--gold)]" /> Fernandina Beach 78°F
            </span>
            <span className="hidden md:inline">{today}</span>
          </div>
          <div className="font-ui flex items-center gap-3">
            <Link to="/media-kit" className="link-underline hidden hover:text-white sm:inline" data-testid="nav-media-kit">Media Kit</Link>
            <Link to="/about" className="link-underline hidden hover:text-white sm:inline" data-testid="nav-about">About Us</Link>
            <Link
              to="/subscribe"
              data-testid="subscribe-cta"
              className="rounded-full bg-[var(--gold)] px-3 py-0.5 font-semibold text-white hover:bg-[#b08f48]"
            >
              Subscribe
            </Link>
            <Link
              to="/admin/login"
              data-testid="login-btn"
              className="rounded-full border border-white/30 px-3 py-0.5 hover:bg-white/10"
            >
              Log In
            </Link>
          </div>
        </div>
      </div>

      {/* Masthead */}
      <div className="border-b border-[var(--paper-border)] bg-[var(--paper)] shadow-sm">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3">
          <button className="lg:hidden" onClick={() => setMobileOpen((v) => !v)} data-testid="mobile-menu-toggle">
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
          <Link to="/classic" className="flex-1 lg:flex-none" data-testid="logo-link">
            <img src={LOGO_URL} alt="News-Leader — Florida's Oldest Weekly Newspaper" className="h-12 object-contain sm:h-16" />
          </Link>
          <div className="flex items-center gap-3">
            <SectionSponsor section="home" variant="masthead" className="hidden sm:flex" />
            <button onClick={() => setSearchOpen((v) => !v)} data-testid="search-toggle" className="text-[var(--navy)] hover:text-[var(--gold)]">
              <Search size={20} />
            </button>
          </div>
        </div>

        {searchOpen && (
          <div className="border-t border-[var(--paper-border)] bg-white px-4 py-3">
            <div className="mx-auto flex max-w-3xl items-center gap-2 rounded-full border border-[var(--paper-border)] px-4 py-2">
              <Search size={18} className="text-slate-400" />
              <input
                data-testid="search-input"
                autoFocus
                placeholder="Search Nassau County news, sports, obituaries…"
                className="font-serif-body w-full bg-transparent text-sm outline-none"
              />
            </div>
          </div>
        )}

        {/* Primary nav */}
        <nav className="hidden border-t border-[var(--paper-border)] bg-[var(--header-navy)] lg:block">
          <div className="no-scrollbar mx-auto flex max-w-7xl items-center justify-center gap-1 overflow-x-auto px-4">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} label={n.label} active={isActive(location.pathname, n.to)} />
            ))}
            <div className="relative" onMouseEnter={() => setMpOpen(true)} onMouseLeave={() => setMpOpen(false)} data-testid="nav-marketplace-wrap">
              <Link to="/marketplace" data-testid="nav-marketplace" className={`font-ui relative flex items-center gap-1 px-3 py-3 text-xs font-semibold uppercase tracking-wider transition-colors ${mpActive ? "text-[var(--gold)]" : "text-white/85 hover:text-white"}`}>
                Marketplace <ChevronDown size={13} />
                {mpActive && <span className="absolute inset-x-2 bottom-0 h-0.5 bg-[var(--gold)]" />}
              </Link>
              {mpOpen && (
                <div className="absolute left-1/2 top-full z-50 w-56 -translate-x-1/2 overflow-hidden rounded-b-lg border border-[var(--paper-border)] bg-white py-1 shadow-xl" data-testid="marketplace-dropdown">
                  {MARKETPLACE.map((m) => (
                    <Link key={m.to} to={m.to} data-testid={`dropdown-${m.label.toLowerCase().replace(/\s+/g, "-")}`} className="block px-4 py-2.5 font-ui text-sm text-slate-700 hover:bg-slate-50 hover:text-[var(--navy)]">
                      {m.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        </nav>
      </div>

      {/* Mobile nav */}
      {mobileOpen && (
        <div className="border-b border-[var(--paper-border)] bg-[var(--header-navy)] lg:hidden" data-testid="mobile-nav">
          <div className="flex flex-col px-4 py-2">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                className="font-ui border-b border-white/10 py-3 text-sm font-semibold uppercase tracking-wider text-white/90"
                data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {n.label}
              </Link>
            ))}
            <p className="font-ui pt-3 pb-1 text-xs font-bold uppercase tracking-wider text-[var(--gold)]">Marketplace</p>
            {MARKETPLACE.map((m) => (
              <Link
                key={m.to}
                to={m.to}
                className="font-ui border-b border-white/10 py-3 pl-3 text-sm uppercase tracking-wider text-white/80"
                data-testid={`mobile-mp-${m.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {m.label}
              </Link>
            ))}
            <Link to="/media-kit" className="font-ui py-3 text-sm uppercase tracking-wider text-white/70">Media Kit</Link>
            <Link to="/about" className="font-ui py-3 text-sm uppercase tracking-wider text-white/70">About Us</Link>
          </div>
        </div>
      )}
    </header>
  );
}

function isActive(path, to) {
  if (to === "/") return path === "/";
  return path.startsWith(to);
}

function NavLink({ to, label, active }) {
  return (
    <Link
      to={to}
      data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className={`font-ui relative px-3 py-3 text-xs font-semibold uppercase tracking-wider transition-colors ${
        active ? "text-[var(--gold)]" : "text-white/85 hover:text-white"
      }`}
    >
      {label}
      {active && <span className="absolute inset-x-2 bottom-0 h-0.5 bg-[var(--gold)]" />}
    </Link>
  );
}
